/**
 * The ways a column can be reduced to one answer, and how that answer reads.
 *
 * Nineteen aggregations, each compatible with some fields and not others. A
 * dashboard's summary widget shows one of them over a whole table; its chart
 * widget shows one per category. The grid's footer will want the same list.
 *
 * **The lists come from the backend's registry, not from the Vue application's.**
 * Both exist, and they disagree: upstream's `viewAggregationTypes.js` leaves
 * `rating` out of six of them, `boolean` out of two, and `created_by` and
 * `last_modified_by` out of `not_empty_count` -- all of which
 * `field_aggregation_registry` accepts. A narrower list is not a safer one: it
 * is an aggregation the server would happily compute that nobody can ask for.
 * These were read out of the live registry rather than transcribed.
 *
 * The *raw type* is here, and it belongs to the grid's footer alone. That
 * endpoint answers a cheaper aggregation and leaves the last step to the
 * client, so eight of the nineteen are fetched as `empty_count` and worked out
 * from it. The dashboard's endpoint answers the aggregation it was asked for
 * and has no arithmetic left to do, which is why it reads the map above and
 * nothing below it.
 *
 * `distribution` is not here either. It is a *view* aggregation with no entry in
 * the field registry, and both dashboard services refuse it by name. Upstream
 * keeps one registry for two consumers and then has to carry a list of which
 * entries the second one cannot use; this is that list, made empty by leaving
 * the entry out.
 */
import type { Field, FieldTypeName } from '@/api/types'
import type { FieldContext } from '../fields/behavior/behavior'
import type { ArrayFormulaType, FormulaType } from '../fields/formula-types'
import { valueText } from '../fields/read'

export type AggregationTypeName =
  | 'count'
  | 'empty_count'
  | 'not_empty_count'
  | 'checked_count'
  | 'not_checked_count'
  | 'empty_percentage'
  | 'not_empty_percentage'
  | 'checked_percentage'
  | 'not_checked_percentage'
  | 'unique_count'
  | 'min'
  | 'max'
  | 'min_date'
  | 'max_date'
  | 'sum'
  | 'average'
  | 'std_dev'
  | 'variance'
  | 'median'

/**
 * A formula result an aggregation accepts.
 *
 * An array formula reports `array` in `formula_type` and its entry type
 * separately, so the backend names the pair as one string -- `array(single_file)`
 * -- and that is reproduced here rather than flattened, because a formula
 * returning a single file and one returning a list of them are different
 * questions.
 */
type AggregatableFormulaType = FormulaType | `array(${ArrayFormulaType})`

/**
 * What the server sends back, which is what decides how it is written down.
 *
 * The backend calls these `integer`, `float` and `field`; they are renamed for
 * what they mean, because all four `float` aggregations are percentages and
 * nothing else about them is floating point.
 */
export type AggregationResult = 'count' | 'percentage' | 'value'

export interface AggregationTypeDef {
  /** How to render the answer. */
  readonly result: AggregationResult
  /** Plain field types the aggregation applies to. */
  readonly fieldTypes: readonly FieldTypeName[]
  /** Formula results it applies to, matched against a formula field's own type. */
  readonly formulaTypes: readonly AggregatableFormulaType[]
}

/** Anything with a value worth counting, which is nearly every field. */
const COUNTABLE: readonly FieldTypeName[] = [
  'boolean',
  'created_by',
  'created_on',
  'date',
  'duration',
  'email',
  'file',
  'last_modified',
  'last_modified_by',
  'link_row',
  'long_text',
  'multiple_select',
  'number',
  'password',
  'phone_number',
  'rating',
  'single_select',
  'text',
  'url',
]

const COUNTABLE_FORMULAS: readonly AggregatableFormulaType[] = [
  'text',
  'char',
  'number',
  'date',
  'boolean',
  'array(single_file)',
]

/**
 * The same list without `boolean`.
 *
 * A checkbox has its own pair -- "percent checked" and "percent unchecked" --
 * which say the same thing in the words that fit it, so offering "percent
 * filled" beside them would be a third name for one of the two.
 */
const FILLABLE = COUNTABLE.filter((type) => type !== 'boolean')
const FILLABLE_FORMULAS = COUNTABLE_FORMULAS.filter((type) => type !== 'boolean')

/** A checkbox, or a formula that answers like one. */
const CHECKABLE = { fieldTypes: ['boolean'], formulaTypes: ['boolean'] } as const

/** Fields whose distinct values can be counted: everything with an ordering. */
const DISTINGUISHABLE: readonly FieldTypeName[] = [
  'created_by',
  'created_on',
  'date',
  'duration',
  'email',
  'last_modified',
  'last_modified_by',
  'long_text',
  'number',
  'phone_number',
  'rating',
  'single_select',
  'text',
  'url',
]

const NUMERIC: readonly FieldTypeName[] = ['number', 'rating']
const NUMERIC_FORMULAS: readonly AggregatableFormulaType[] = ['number']
const DATED: readonly FieldTypeName[] = ['created_on', 'date', 'last_modified']

/**
 * Every aggregation, in the order the backend registers them.
 *
 * That order is the one a menu shows: the counts, then the percentages, then
 * the arithmetic. Alphabetical would separate "percent empty" from "percent
 * filled" and put "average" first.
 */
export const AGGREGATION_TYPES = {
  count: {
    result: 'count',
    fieldTypes: COUNTABLE,
    formulaTypes: COUNTABLE_FORMULAS,
  },
  empty_count: {
    result: 'count',
    fieldTypes: COUNTABLE,
    formulaTypes: COUNTABLE_FORMULAS,
  },
  not_empty_count: {
    result: 'count',
    fieldTypes: COUNTABLE,
    formulaTypes: COUNTABLE_FORMULAS,
  },
  checked_count: { result: 'count', ...CHECKABLE },
  not_checked_count: { result: 'count', ...CHECKABLE },
  empty_percentage: {
    result: 'percentage',
    fieldTypes: FILLABLE,
    formulaTypes: FILLABLE_FORMULAS,
  },
  not_empty_percentage: {
    result: 'percentage',
    fieldTypes: FILLABLE,
    formulaTypes: FILLABLE_FORMULAS,
  },
  checked_percentage: { result: 'percentage', ...CHECKABLE },
  not_checked_percentage: { result: 'percentage', ...CHECKABLE },
  unique_count: {
    result: 'count',
    fieldTypes: DISTINGUISHABLE,
    formulaTypes: ['text', 'char', 'number', 'date'],
  },
  min: {
    result: 'value',
    fieldTypes: ['autonumber', 'duration', ...NUMERIC],
    formulaTypes: NUMERIC_FORMULAS,
  },
  max: {
    result: 'value',
    fieldTypes: ['autonumber', 'duration', ...NUMERIC],
    formulaTypes: NUMERIC_FORMULAS,
  },
  min_date: { result: 'value', fieldTypes: DATED, formulaTypes: ['date'] },
  max_date: { result: 'value', fieldTypes: DATED, formulaTypes: ['date'] },
  sum: {
    result: 'value',
    fieldTypes: ['duration', ...NUMERIC],
    formulaTypes: NUMERIC_FORMULAS,
  },
  average: { result: 'value', fieldTypes: NUMERIC, formulaTypes: NUMERIC_FORMULAS },
  std_dev: { result: 'value', fieldTypes: NUMERIC, formulaTypes: NUMERIC_FORMULAS },
  variance: { result: 'value', fieldTypes: NUMERIC, formulaTypes: NUMERIC_FORMULAS },
  median: { result: 'value', fieldTypes: NUMERIC, formulaTypes: NUMERIC_FORMULAS },
} as const satisfies Record<AggregationTypeName, AggregationTypeDef>

/** The aggregations in registration order. */
export const AGGREGATION_TYPE_ORDER = Object.keys(
  AGGREGATION_TYPES,
) as AggregationTypeName[]

export function isAggregationTypeName(value: unknown): value is AggregationTypeName {
  return typeof value === 'string' && value in AGGREGATION_TYPES
}

/**
 * Whether an aggregation can be asked of a field.
 *
 * Only a formula, rollup, count or lookup field carries a `formula_type`, so
 * reading it is already the test the backend makes by asking whether the field
 * is a formula one first.
 */
export function isAggregationCompatible(
  type: AggregationTypeName,
  field: Field,
): boolean {
  // Widened deliberately: the map is `as const`, so each entry's lists are
  // tuples of literals and `includes` on the union of them accepts nothing.
  const { fieldTypes, formulaTypes }: AggregationTypeDef = AGGREGATION_TYPES[type]
  if (fieldTypes.includes(field.type)) return true

  const { formula_type: formula, array_formula_type: entries } = field as {
    formula_type?: string
    array_formula_type?: string
  }
  if (formula === undefined) return false
  return (
    formulaTypes.includes(formula as AggregatableFormulaType) ||
    (entries !== undefined &&
      formulaTypes.includes(`array(${entries})` as AggregatableFormulaType))
  )
}

/** The aggregations a field can be asked for, in menu order. */
export function aggregationsForField(field: Field): AggregationTypeName[] {
  return AGGREGATION_TYPE_ORDER.filter((type) => isAggregationCompatible(type, field))
}

/**
 * An aggregation's answer as the text a reader sees.
 *
 * A count is a whole number and reads as one. A percentage arrives on a scale
 * of a hundred and is rounded, because the extra digits are noise over a table
 * whose row count moves. Everything else is a value in the field's own terms --
 * the earliest date in its format, a sum of durations as a duration -- so it is
 * handed to the same rendering a cell of that field would get.
 */
export function aggregationText(
  type: AggregationTypeName,
  field: Field,
  value: unknown,
  context?: FieldContext,
): string {
  const { result }: AggregationTypeDef = AGGREGATION_TYPES[type]
  if (result === 'value') return valueText(field.type, field, value, context)

  // A number or nothing. The server answers null for an aggregation it could
  // not compute -- over no rows at all -- and that is not a zero to show.
  if (typeof value !== 'number' || !Number.isFinite(value)) return ''
  return result === 'percentage' ? `${Math.round(value)}%` : String(value)
}

// ---------------------------------------------------------------------------
// What the grid's footer asks for, which is not what it shows
// ---------------------------------------------------------------------------

/**
 * The aggregations the grid view endpoint actually computes.
 *
 * A subset, because that endpoint reads what each field's options have stored
 * and answers with it -- there is no list of aggregations in the request. The
 * other nine are arithmetic over one of these.
 */
export type RawAggregationName = Extract<
  AggregationTypeName,
  | 'count'
  | 'empty_count'
  | 'unique_count'
  | 'min'
  | 'max'
  | 'sum'
  | 'average'
  | 'std_dev'
  | 'variance'
  | 'median'
>

/**
 * Which aggregation the server is asked for, per aggregation shown.
 *
 * **Eight of the nineteen are `empty_count`.** That is the whole point of the
 * distinction rather than a curiosity: "filled", "empty", "checked",
 * "unchecked" and the four percentages of those are one number and eight ways
 * of reading it, so switching a footer between them costs no request at all --
 * the number is already in hand. Only a change of *raw* type is a change of
 * question.
 *
 * `not_checked_count` needs no arithmetic even though it is derived: a checkbox
 * that is empty is a checkbox that is unchecked, so the count comes back
 * already meaning what it says.
 */
export const AGGREGATION_RAW_TYPE = {
  count: 'count',
  empty_count: 'empty_count',
  not_empty_count: 'empty_count',
  checked_count: 'empty_count',
  not_checked_count: 'empty_count',
  empty_percentage: 'empty_count',
  not_empty_percentage: 'empty_count',
  checked_percentage: 'empty_count',
  not_checked_percentage: 'empty_count',
  unique_count: 'unique_count',
  min: 'min',
  max: 'max',
  min_date: 'min',
  max_date: 'max',
  sum: 'sum',
  average: 'average',
  std_dev: 'std_dev',
  variance: 'variance',
  median: 'median',
} as const satisfies Record<AggregationTypeName, RawAggregationName>

/**
 * The raw aggregations a *view* will not store.
 *
 * `count` alone, and not because a grid has no use for it: the backend's
 * registry marks `CountViewAggregationType` as not allowed in a view, so the
 * field-options endpoint refuses a column asking for it and answers
 * `ERROR_REQUEST_BODY_VALIDATION`. It stays in the list above because a
 * dashboard's summary widget asks a different endpoint, and that one computes
 * it.
 */
const REFUSED_BY_VIEWS: readonly RawAggregationName[] = ['count']

/**
 * The aggregations a grid's footer can offer for a field.
 *
 * Narrower than `aggregationsForField`, which answers for both consumers.
 */
export function viewAggregationsForField(field: Field): AggregationTypeName[] {
  return aggregationsForField(field).filter(
    (type) => !REFUSED_BY_VIEWS.includes(AGGREGATION_RAW_TYPE[type]),
  )
}

/** The arithmetic each derived aggregation does over `empty_count`. */
const FROM_EMPTY_COUNT: Partial<
  Record<AggregationTypeName, (empty: number, rowCount: number) => number>
> = {
  not_empty_count: (empty, rows) => rows - empty,
  checked_count: (empty, rows) => rows - empty,
  empty_percentage: (empty, rows) => (empty / rows) * 100,
  not_checked_percentage: (empty, rows) => (empty / rows) * 100,
  not_empty_percentage: (empty, rows) => ((rows - empty) / rows) * 100,
  checked_percentage: (empty, rows) => ((rows - empty) / rows) * 100,
}

/**
 * What to show, from what the server said and how many rows it said it over.
 *
 * The row count is the aggregation's own, not the view's: both arrive in the
 * same response, because a count taken against a different set of rows from the
 * one that was counted would make every percentage wrong by however much the
 * table moved in between.
 *
 * Null rather than zero over no rows at all. "0% filled" for an empty table is
 * a statement about nothing, and the division that produces it is a division by
 * zero.
 */
export function aggregationValue(
  type: AggregationTypeName,
  raw: unknown,
  rowCount: number,
): unknown {
  const derive = FROM_EMPTY_COUNT[type]
  if (derive === undefined) return raw

  // The endpoint sends the literal string "NaN" for a decimal it could not
  // compute, so "is this a number" is a real question rather than a formality.
  if (typeof raw !== 'number' || !Number.isFinite(raw) || rowCount === 0) return null
  return derive(raw, rowCount)
}
